import { Component } from '@theme/component';

// How long a size button stays in its "added" state after a successful click.
const ADDED_STATE_DURATION = 1800;

/**
 * An inline size picker rendered over a product card's media.
 *
 * Selecting a value writes that variant id into the product form's hidden `id`
 * input and submits the form, so the actual cart request, events and error
 * handling all stay inside ProductFormComponent — this component only decides
 * which variant is being added.
 *
 * Only rendered for products with a single option (see quick-add-sizes.liquid).
 */
class QuickAddSizesComponent extends Component {
  /** @type {AbortController | null} */
  #dismissController = null;

  /** @type {number | undefined} */
  #addedTimeout;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#dismissController?.abort();
    clearTimeout(this.#addedTimeout);
  }

  /**
   * Mobile only: reveals or hides the size list behind the quick add button.
   * @param {Event} event - The click event.
   */
  toggleSizes = (event) => {
    event.preventDefault();

    if (this.hasAttribute('open')) {
      this.closeSizes();
      return;
    }

    this.setAttribute('open', '');
    this.#setTriggerExpanded(true);

    // Dismiss when the shopper taps anywhere outside this card.
    this.#dismissController = new AbortController();
    document.addEventListener(
      'pointerdown',
      (dismissEvent) => {
        if (dismissEvent.target instanceof Node && this.contains(dismissEvent.target)) return;
        this.closeSizes();
      },
      { signal: this.#dismissController.signal }
    );
  };

  closeSizes = () => {
    this.removeAttribute('open');
    this.#setTriggerExpanded(false);
    this.#dismissController?.abort();
    this.#dismissController = null;
  };

  /**
   * Adds the clicked variant straight to the cart — there is no separate add button.
   * @param {Event} event - The click event.
   */
  select = (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    event.preventDefault();

    const form = this.querySelector('form');
    const variantInput = this.querySelector('input[name="id"]');

    if (!(form instanceof HTMLFormElement) || !(variantInput instanceof HTMLInputElement)) return;

    variantInput.value = button.dataset.variantId ?? '';
    variantInput.disabled = false;

    // ProductFormComponent is bound to this form's submit event and owns the request.
    form.requestSubmit();

    this.#markAdded(button);
    this.closeSizes();
  };

  /**
   * @param {boolean} expanded
   */
  #setTriggerExpanded(expanded) {
    this.querySelector('.quick-add-sizes__trigger')?.setAttribute('aria-expanded', String(expanded));
  }

  /**
   * @param {HTMLButtonElement} button - The size button that was selected.
   */
  #markAdded(button) {
    for (const previous of this.querySelectorAll('[data-added]')) previous.removeAttribute('data-added');

    button.setAttribute('data-added', 'true');

    clearTimeout(this.#addedTimeout);
    this.#addedTimeout = setTimeout(() => button.removeAttribute('data-added'), ADDED_STATE_DURATION);
  }
}

if (!customElements.get('quick-add-sizes-component')) {
  customElements.define('quick-add-sizes-component', QuickAddSizesComponent);
}